import { useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
    type BaseActivatedPanelProps,
    ManualSelectionItem,
    type SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    selectActiveLeadsType,
    selectSelectedRowDomains,
    setIsRowSelectionEnabled,
    useClearAndCloseRowsSelection,
} from "app/pages/sales-intelligence/modules/leads-search";
import { leadsCostTypeMapper } from "app/pages/sales-intelligence/modules/leads-search/utils/quota";
import { useLeadsExportCost } from "app/pages/sales-intelligence/modules/quota/hooks/useLeadsExportCost";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import LegacyWebsitesExcelExportPanelView from "app/pages/sales-intelligence/sub-modules/company-search/components/LegacyWebsitesExcelExportPanel/LegacyWebsitesExcelExportPanelView";
import WebsitesExcelExportPanelView from "app/pages/sales-intelligence/sub-modules/company-search/components/WebsitesExcelExportPanel/WebsitesExcelExportPanelView";
import { hideExportSuccessToasts } from "app/pages/sales-intelligence/sub-modules/contacts-search/helpers/exportFlow";
import createListLeadsSelectionDropdown from "app/pages/sales-intelligence/sub-modules/opportunities/models/selection-dropdown/factory";

import { useLeadsResultsActionsState } from "../../../hooks/useLeadsResultsActionsState";
import { useExcelExportMutation } from "../../../query/mutations/useExcelExportMutation";

const ExcelExportPanelContainer = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(hideExportSuccessToasts());
    }, []);

    const selectedRowDomains = useSelector(selectSelectedRowDomains);
    const activeLeadsType = useSelector(selectActiveLeadsType);

    const { mutateAsync, isPending: isExcelExportPending } = useExcelExportMutation(activeLeadsType);
    const { areActionsDisabled, numberOfResults, selectedColumnTypes } = useLeadsResultsActionsState(activeLeadsType);

    const exportCostState = useLeadsExportCost({
        columnTypes: selectedColumnTypes,
        costTypeMapper: leadsCostTypeMapper,
    });

    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection();

    const leadsSelectionDropdown = useMemo(() => createListLeadsSelectionDropdown(activeLeadsType), [activeLeadsType]);

    const handleDeactivate = (): void => {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    };

    const handleSelectionChange = (item: Readonly<SelectionDropdownItem>): void => {
        if (item.id === ManualSelectionItem.ID) {
            dispatch(setIsRowSelectionEnabled(true));

            return;
        }

        clearAndCloseRowsSelection();
    };

    const handleSubmit = async (item: Readonly<SelectionDropdownItem>): Promise<void> => {
        trackingService.trackMultiSelectorExportClick(
            "excel",
            exportCostState.quota.data.remaining,
            item.alias,
            item.value,
        );

        handleDeactivate();
        await mutateAsync(item.id === ManualSelectionItem.ID ? selectedRowDomains : item.value);
    };

    if (salesSettingsHelper.isDataCreditsEnabled) {
        return (
            <WebsitesExcelExportPanelView
                isDisabled={areActionsDisabled || isExcelExportPending}
                isLoading={isExcelExportPending}
                numberOfSelectedItems={selectedRowDomains.length}
                numberOfRemainingExports={exportCostState.quota.data.remaining}
                numberOfResults={numberOfResults}
                numberOfTableColumns={0}
                exportCostState={exportCostState}
                selectionDropdown={leadsSelectionDropdown}
                onDeactivate={handleDeactivate}
                onSelectionChange={handleSelectionChange}
                onSubmit={handleSubmit}
            />
        );
    }

    return (
        <LegacyWebsitesExcelExportPanelView
            isDisabled={areActionsDisabled || isExcelExportPending}
            isLoading={isExcelExportPending}
            numberOfSelectedItems={selectedRowDomains.length}
            numberOfRemainingExports={exportCostState.quota.data.remaining}
            numberOfResults={numberOfResults}
            numberOfTableColumns={0}
            selectionDropdown={leadsSelectionDropdown}
            onDeactivate={handleDeactivate}
            onSelectionChange={handleSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default ExcelExportPanelContainer;
